import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import FormSelect from "../../components/UI/FormSelect";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import {
  getPatientById,
  getPatientChart,
  createPatientChart,
  updatePatientChart,
} from "../../services/patientService";
import {
  UserIcon,
  DocumentsIcon,
  CalendarIcon,
} from "../../Icons";
import { STATUS_LABELS } from "./components/StatusBadge";
import CollapsibleSection from "./components/CollapsibleSection";
import {
  displayDateUS,
  formatDateForInput,
  toIsoDate,
} from "../../utils/dateUtils";
import { PATIENT_PAGE_STACK } from "./patientPageLayout";
import { canEditDocsNotes } from "./patientPermissions";
import { patientSectionTones, PATIENT_THEME } from "./patientTheme";

const EPISODE_OFFSET_DAYS = 59;

const dateLabelClass = "block text-[11px] font-medium text-slate-600 mb-0.5";
const dateInputClass = `block w-full rounded-md border border-slate-300 bg-white text-slate-900 text-sm py-1.5 px-2.5 outline-none ${PATIENT_THEME.focus}`;

const btnCancel =
  "inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed box-border";
const btnPrimary = `inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed box-border border ${PATIENT_THEME.button.outline}`;

const SECTION_TONES = patientSectionTones(["patient", "chart"]);

const addDaysYmd = (ymd, days) => {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};

const ymdToDisplay = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).slice(0, 10).split("-");
  if (!y || !m || !d) return "";
  return `${m}/${d}/${y}`;
};

const parseCmp = (a, b) => {
  const aa = formatDateForInput(a);
  const bb = formatDateForInput(b);
  if (!aa || !bb) return 0;
  return aa.localeCompare(bb);
};

const Field = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </p>
    <p className="text-sm text-slate-900 font-medium mt-0.5 break-words leading-snug">
      {value || "—"}
    </p>
  </div>
);

/**
 * Add or edit a patient chart — layout mirrors Add Patient (header actions top-right).
 */
const PatientChartFormPage = () => {
  const { id, chartId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(chartId);
  const [patient, setPatient] = useState(null);
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const canEdit = canEditDocsNotes(currentUser?.role?.name);

  const methods = useForm({
    defaultValues: {
      status: "PreAdmit",
      startDate: "",
      dischargeDate: "",
      episodeStartDate: "",
      episodeEndDate: "",
    },
  });
  const { handleSubmit, watch, setValue, reset, register } = methods;
  const startDate = watch("startDate");
  const episodeStartDate = watch("episodeStartDate");
  const chartStartYmd = formatDateForInput(startDate) || undefined;
  const episodeStartYmd = formatDateForInput(episodeStartDate) || undefined;

  const backToCharts = () => navigate(`/patients/${id}/charts`);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const patientRes = await getPatientById(id);
      const p =
        patientRes.result?.patient ||
        patientRes.patient ||
        patientRes.result ||
        null;
      setPatient(p);

      if (isEdit) {
        const chartRes = await getPatientChart(id, chartId);
        const c = chartRes.result?.chart || chartRes.chart || null;
        setChart(c);
        const firstEp = [...(c?.episodes || [])].sort(
          (a, b) => a.episodeNumber - b.episodeNumber,
        )[0];
        reset({
          status: c?.status || "PreAdmit",
          startDate: displayDateUS(c?.startDate) || "",
          dischargeDate: displayDateUS(c?.dischargeDate) || "",
          episodeStartDate: displayDateUS(firstEp?.startDate) || "",
          episodeEndDate: displayDateUS(firstEp?.endDate) || "",
        });
      } else {
        const today = formatDateForInput(new Date());
        const startYmd = formatDateForInput(p?.admissionDate) || today;
        const epEndYmd = addDaysYmd(startYmd, EPISODE_OFFSET_DAYS);
        reset({
          status: "PreAdmit",
          startDate: ymdToDisplay(startYmd),
          dischargeDate: "",
          episodeStartDate: ymdToDisplay(startYmd),
          episodeEndDate: ymdToDisplay(epEndYmd),
        });
      }
    } catch (err) {
      toast.error(err.message || "Failed to load");
      navigate(`/patients/${id}/charts`);
    } finally {
      setLoading(false);
    }
  }, [id, chartId, isEdit, navigate, reset]);

  useEffect(() => {
    load();
  }, [load]);

  // Chart start drives episode start (add mode).
  useEffect(() => {
    if (isEdit || !startDate) return;
    const ymd = formatDateForInput(startDate);
    if (!ymd) return;
    setValue("episodeStartDate", ymdToDisplay(ymd));
  }, [startDate, isEdit, setValue]);

  // Episode start drives default 60-day end (add mode); user can still override end.
  useEffect(() => {
    if (isEdit || !episodeStartDate) return;
    const ymd = formatDateForInput(episodeStartDate);
    if (!ymd) return;
    setValue(
      "episodeEndDate",
      ymdToDisplay(addDaysYmd(ymd, EPISODE_OFFSET_DAYS)),
    );
  }, [episodeStartDate, isEdit, setValue]);

  const patientName = useMemo(() => {
    if (!patient) return "—";
    return (
      [patient.lastName, patient.firstName].filter(Boolean).join(", ") || "—"
    );
  }, [patient]);

  const validateDates = (values) => {
    const chartStart = formatDateForInput(values.startDate);
    const chartDischarge = formatDateForInput(values.dischargeDate);
    const epStart = formatDateForInput(values.episodeStartDate);
    const epEnd = formatDateForInput(values.episodeEndDate);

    if (!chartStart) {
      toast.error("Chart start date is required");
      return false;
    }
    if (chartDischarge && parseCmp(chartDischarge, chartStart) < 0) {
      toast.error("Discharge date must be on or after chart start date");
      return false;
    }
    if (!isEdit) {
      if (!epStart || !epEnd) {
        toast.error("Episode start and end dates are required");
        return false;
      }
      if (parseCmp(epEnd, epStart) < 0) {
        toast.error("Episode end must be on or after episode start");
        return false;
      }
      if (parseCmp(epStart, chartStart) < 0) {
        toast.error("Episode must start on or after the chart start date");
        return false;
      }
      if (chartDischarge && parseCmp(epEnd, chartDischarge) > 0) {
        toast.error("Episode must end on or before the chart discharge date");
        return false;
      }
    }
    return true;
  };

  const onSubmit = async (values) => {
    if (!canEdit) {
      toast.error("You do not have permission to manage charts");
      return;
    }
    if (!validateDates(values)) return;

    setSaving(true);
    try {
      if (isEdit) {
        await updatePatientChart(id, chartId, {
          status: values.status,
          startDate: toIsoDate(values.startDate) || null,
          dischargeDate: values.dischargeDate
            ? toIsoDate(values.dischargeDate)
            : null,
        });
        toast.success("Chart updated");
      } else {
        await createPatientChart(id, {
          status: values.status,
          startDate: toIsoDate(values.startDate),
          dischargeDate: values.dischargeDate
            ? toIsoDate(values.dischargeDate)
            : null,
          episodeStartDate: toIsoDate(values.episodeStartDate),
          episodeEndDate: toIsoDate(values.episodeEndDate),
        });
        toast.success("Chart created");
      }
      backToCharts();
    } catch (err) {
      toast.error(err.message || "Failed to save chart");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`${PATIENT_PAGE_STACK} animate-pulse`}>
        <div className="h-10 bg-slate-100 border border-slate-200 rounded-lg" />
        <div className="h-64 bg-slate-100 border border-slate-200 rounded-lg" />
      </div>
    );
  }

  if (!patient) return null;

  const addressLine = [patient.address, patient.city, patient.state, patient.zip]
    .filter(Boolean)
    .join(", ");

  const title = isEdit
    ? `Edit Chart #${chart?.chartNumber || ""}`
    : "Add New Chart";
  const subtitle = isEdit
    ? "Update chart start, discharge, and status · dates mm/dd/yyyy"
    : "Creates Chart + Episode #1 · default 60-day window · dates mm/dd/yyyy";

  return (
    <div className={`relative ${PATIENT_PAGE_STACK} min-w-0 overflow-x-hidden`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 pr-1">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold text-slate-800">{title}</h1>
          <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            type="button"
            disabled={saving}
            onClick={backToCharts}
            className={btnCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canEdit || saving}
            onClick={handleSubmit(onSubmit)}
            className={btnPrimary}
          >
            {saving ? (
              <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : null}
            {saving
              ? "Saving…"
              : isEdit
                ? "Save Chart"
                : "Create Chart"}
          </button>
        </div>
      </div>

      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 min-w-0"
          noValidate
        >
          <CollapsibleSection
            title="Patient Information"
            subtitle="Existing details stay the same — chart dates are set below."
            icon={<UserIcon size={14} />}
            tone={SECTION_TONES.patient}
            defaultOpen
            bodyClassName="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-3 min-w-0"
          >
            <Field label="Patient" value={patientName} />
            <Field label="MRN" value={patient.mrn || patient.patientNumber} />
            <Field
              label="Status"
              value={STATUS_LABELS[patient.status] || patient.status}
            />
            <Field
              label="Date of Birth"
              value={displayDateUS(patient.dateOfBirth)}
            />
            <Field label="Gender" value={patient.gender} />
            <Field
              label="Prior SOC date"
              value={displayDateUS(patient.admissionDate)}
            />
            {patient.eocDate ? (
              <Field
                label="Prior EOC date"
                value={displayDateUS(patient.eocDate)}
              />
            ) : null}
            <Field label="Phone" value={patient.phone} />
            <Field label="Email" value={patient.email} />
            <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4 min-w-0">
              <Field label="Address" value={addressLine} />
            </div>
            {(patient.pharmacyName || patient.pharmacyPhone) && (
              <>
                <Field label="Pharmacy" value={patient.pharmacyName} />
                <Field label="Pharmacy phone" value={patient.pharmacyPhone} />
                <Field label="Pharmacy type" value={patient.pharmacyType} />
              </>
            )}
            {(patient.hospitalName || patient.hospitalPhone) && (
              <>
                <Field label="Hospital" value={patient.hospitalName} />
                <Field label="Hospital phone" value={patient.hospitalPhone} />
                <Field label="Hospital type" value={patient.hospitalType} />
              </>
            )}
          </CollapsibleSection>

          <CollapsibleSection
            title={isEdit ? "Chart Details" : "New Chart Details"}
            subtitle={
              isEdit
                ? "Update chart start, discharge, and status."
                : "Creates Chart + Episode #1 (default 60-day window)."
            }
            icon={
              isEdit ? (
                <DocumentsIcon size={14} />
              ) : (
                <CalendarIcon size={14} />
              )
            }
            tone={SECTION_TONES.chart}
            defaultOpen
            bodyClassName="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 min-w-0"
          >
            <FormSelect
              size="sm"
              label="Chart status"
              name="status"
              register={register}
              options={[
                { value: "PreAdmit", label: STATUS_LABELS.PreAdmit },
                { value: "Admit", label: STATUS_LABELS.Admit },
                { value: "Active", label: STATUS_LABELS.Active },
                { value: "Hold", label: STATUS_LABELS.Hold },
                ...(isEdit
                  ? [
                      {
                        value: "Inactive",
                        label: STATUS_LABELS.Inactive,
                      },
                      {
                        value: "Discharged",
                        label: STATUS_LABELS.Discharged,
                      },
                      {
                        value: "NonAdmit",
                        label: STATUS_LABELS.NonAdmit,
                      },
                      {
                        value: "Deceased",
                        label: STATUS_LABELS.Deceased,
                      },
                    ]
                  : []),
              ]}
            />
            <InputDatePicker
              label="Chart start date"
              name="startDate"
              formatSeparator="/"
              allowManualEntry
              allowFuture
              isRequired
              labelClassName={dateLabelClass}
              inputClassName={dateInputClass}
            />
            <InputDatePicker
              label="Chart discharge date"
              name="dischargeDate"
              formatSeparator="/"
              allowManualEntry
              allowFuture
              min={chartStartYmd}
              labelClassName={dateLabelClass}
              inputClassName={dateInputClass}
            />
            {!isEdit && (
              <>
                <InputDatePicker
                  label="Episode start date"
                  name="episodeStartDate"
                  formatSeparator="/"
                  allowManualEntry
                  allowFuture
                  isRequired
                  min={chartStartYmd}
                  labelClassName={dateLabelClass}
                  inputClassName={dateInputClass}
                />
                <InputDatePicker
                  label="Episode end date"
                  name="episodeEndDate"
                  formatSeparator="/"
                  allowManualEntry
                  allowFuture
                  isRequired
                  min={episodeStartYmd || chartStartYmd}
                  labelClassName={dateLabelClass}
                  inputClassName={dateInputClass}
                />
              </>
            )}
          </CollapsibleSection>
        </form>
      </FormProvider>
    </div>
  );
};

export default PatientChartFormPage;
